import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Check, Eye, EyeOff, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { api, errMsg, fieldErrs, formatDate, todayIso } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Stars } from "@/components/site/ReviewsSection";
import { StarPicker } from "@/components/site/StarPicker";
import { AdminLayout, btnPrimary, btnSecondary, ConfirmDelete, FieldError, inputCls, Panel } from "@/components/admin/AdminLayout";
import { Tabs } from "@/pages/admin/MessagesPage";
import { GetReviewsBox } from "@/components/admin/GetReviewsBox";

const REQUIRED = "This field is required";
const blank = () => ({ name: "", rating: 0, text: "", vehicle: "", date: todayIso() });
const TABS = [["pending", "Pending"], ["published", "Published"], ["hidden", "Hidden"]];

const ReviewForm = ({ initial, onSubmit, onCancel, prefix, submitLabel, autoFocus }) => {
  const [f, setF] = useState(initial);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!f.name.trim()) errs.name = REQUIRED;
    if (!f.rating) errs.rating = REQUIRED;
    if (!f.text.trim()) errs.text = REQUIRED;
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    const res = await onSubmit({ name: f.name, rating: f.rating, text: f.text, vehicle: f.vehicle, date: f.date || todayIso() });
    setBusy(false);
    if (res?.errors) setErrors(res.errors);
    else if (res && !onCancel) setF(blank());
  };
  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid={`${prefix}-form`}>
      <div>
        <Label>Customer name</Label>
        <Input autoFocus={autoFocus} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={`${inputCls} mt-1.5`} data-testid={`${prefix}-name-input`} />
        <FieldError msg={errors.name} testId={`${prefix}-name-error`} />
      </div>
      <div>
        <Label>Rating</Label>
        <StarPicker value={f.rating} onChange={(rating) => setF({ ...f, rating })} prefix={prefix} />
        <FieldError msg={errors.rating} testId={`${prefix}-rating-error`} />
      </div>
      <div>
        <Label>Review</Label>
        <Textarea rows={3} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} className="mt-1.5 bg-[#121214] border-zinc-700 text-base" data-testid={`${prefix}-text-input`} />
        <FieldError msg={errors.text} testId={`${prefix}-text-error`} />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label>Vehicle (optional)</Label>
          <Input value={f.vehicle} placeholder="e.g. 2021 Honda Civic" onChange={(e) => setF({ ...f, vehicle: e.target.value })} className={`${inputCls} mt-1.5`} data-testid={`${prefix}-vehicle-input`} />
        </div>
        <div>
          <Label>Date</Label>
          <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={`${inputCls} mt-1.5`} data-testid={`${prefix}-date-input`} />
        </div>
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={busy} className={btnPrimary} data-testid={`${prefix}-submit-button`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : !onCancel && <Plus className="h-4 w-4" />} {submitLabel}
        </Button>
        {onCancel && <Button type="button" onClick={onCancel} className={btnSecondary} data-testid={`${prefix}-cancel-button`}>Cancel</Button>}
      </div>
    </form>
  );
};

const ReviewItem = ({ r, onCall, onEdit }) => (
  <>
    <div className="flex flex-wrap items-center gap-2">
      <p className="break-words font-semibold text-zinc-100" data-testid="admin-review-name">{r.name}</p>
      <Stars value={r.rating} />
      <span className={`rounded-sm px-2 py-0.5 text-xs ${r.source === "customer" ? "bg-[#3b82f6]/20 text-[#93c5fd]" : "bg-zinc-800 text-zinc-300"}`} data-testid="admin-review-source">
        {r.source === "customer" ? "Submitted by customer" : "Added by you"}
      </span>
      {r.email_alert === "failed" && <span className="inline-flex items-center gap-1 rounded-sm bg-[#C8322B]/20 px-2 py-0.5 text-xs font-semibold text-[#ff8a80]" data-testid="email-alert-failed-badge"><AlertTriangle className="h-3 w-3" /> Email alert not sent</span>}
    </div>
    <p className="mt-2 whitespace-pre-line break-words text-zinc-300" data-testid="admin-review-text">{r.text}</p>
    <p className="mt-1 text-sm text-zinc-500">{r.vehicle && `${r.vehicle} · `}{formatDate(r.date)}</p>
    <div className="mt-3 grid grid-cols-2 gap-2 sm:flex">
      {r.status === "pending" && <Button className={`${btnPrimary} col-span-2 sm:flex-1`} onClick={() => onCall(() => api.post(`/admin/reviews/${r.id}/approve`), "Review approved — now live")} data-testid="admin-review-approve-button"><Check className="h-4 w-4" /> Approve</Button>}
      {r.status === "published" && <Button className={btnSecondary} onClick={() => onCall(() => api.put(`/admin/reviews/${r.id}`, { status: "hidden" }), "Review hidden")} data-testid="admin-review-hide-button"><EyeOff className="h-4 w-4" /> Hide</Button>}
      {r.status === "hidden" && <Button className={btnSecondary} onClick={() => onCall(() => api.put(`/admin/reviews/${r.id}`, { status: "published" }), "Review shown")} data-testid="admin-review-show-button"><Eye className="h-4 w-4" /> Show</Button>}
      <Button className={btnSecondary} onClick={onEdit} data-testid="admin-review-edit-button"><Pencil className="h-4 w-4" /> Edit</Button>
      <ConfirmDelete description={`The review from ${r.name} will be deleted.`} onConfirm={() => onCall(() => api.delete(`/admin/reviews/${r.id}`), "Review deleted")}
        trigger={<Button className={`${btnSecondary} text-[#ff8a80]`} data-testid="admin-review-delete-button"><Trash2 className="h-4 w-4" /> Delete</Button>} />
    </div>
  </>
);

export default function ReviewsPage() {
  const [params, setParams] = useSearchParams();
  const adding = params.get("add") === "1";
  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : adding ? "published" : "pending";
  const [reviews, setReviews] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(adding);
  const addRef = useRef(null);
  const load = () => api.get("/admin/reviews").then((r) => setReviews(r.data));
  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    if (adding) addRef.current?.scrollIntoView({ block: "start" });
  }, [adding]);

  const call = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      await load();
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return { errors: fieldErrs(e) };
    }
  };
  const counts = Object.fromEntries(TABS.map(([k]) => [k, reviews.filter((r) => r.status === k).length]));
  const shown = reviews.filter((r) => r.status === tab);
  const setTab = (t) => setParams({ tab: t }, { replace: true });

  return (
    <AdminLayout title="Reviews" testId="admin-reviews-page">
      <GetReviewsBox />
      <div ref={addRef}>
        {showAdd ? (
          <Panel title="Add a review" testId="add-review-panel">
            <ReviewForm initial={blank()} prefix="review-add" submitLabel="Add review" autoFocus={adding}
              onSubmit={async (v) => {
                const res = await call(() => api.post("/admin/reviews", v), "Review added — it's live on the website");
                if (res === true) setTab("published");
                return res;
              }} />
          </Panel>
        ) : (
          <Button onClick={() => setShowAdd(true)} className={`${btnPrimary} mb-6 w-full`} data-testid="show-add-review-button"><Plus className="h-4 w-4" /> Add a review yourself</Button>
        )}
      </div>
      <Tabs tabs={TABS} active={tab} onChange={setTab} counts={counts} prefix="reviews" />
      {shown.length === 0 && <p className="rounded-lg border border-dashed border-zinc-700 p-8 text-center text-zinc-400" data-testid="admin-reviews-empty">No {tab} reviews.</p>}
      <ul className="space-y-3" data-testid="admin-review-list">
        {shown.map((r) => (
          <li key={r.id} className="rounded-lg border border-zinc-800 bg-[#1A1A1E] p-4" data-testid={`admin-review-${r.id}`}>
            {editing === r.id ? (
              <ReviewForm initial={r} prefix="review-edit" submitLabel="Save" onCancel={() => setEditing(null)}
                onSubmit={async (v) => {
                  const res = await call(() => api.put(`/admin/reviews/${r.id}`, v), "Review saved");
                  if (res === true) setEditing(null);
                  return res;
                }} />
            ) : (
              <ReviewItem r={r} onCall={call} onEdit={() => setEditing(r.id)} />
            )}
          </li>
        ))}
      </ul>
    </AdminLayout>
  );
}
